/* Canonical collection behavior. Shopify-rendered markup/config remain authoritative. */
(function () {
  'use strict';
  const storefront = window.BSStorefront;
  if (storefront.collection) return;

  const mountedRoots = new WeakSet();
  // A sent non-idempotent mutation outlives a mount. Keep only its pending state
  // and current renderer, keyed by the actual form; never abort or retry it.
  const submissions = new WeakMap();
  function sendQuickAdd(record, body) {
    const finish = (success) => {
      record.pending = false;
      if (record.notify) record.notify(success);
    };
    let request;
    try {
      request = fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: 'application/json'
        },
        body
      });
    } catch (error) {
      finish(false);
      return;
    }
    Promise.resolve(request).then(response => finish(response.ok), () => finish(false));
  }

  // Explicit legacy global debt, separate from header-clock.js's 30s clock.
  // These IDs are absent from the current canonical section AND layout. Keep
  // the original GET/60s cadence and formatting, but no null-target exception.
  const clockRoots = new Set();
  const clockRequests = new Set();
  let clockTimer = null;
  let clockGeneration = 0;
  function stopLegacyClock() {
    clockGeneration += 1;
    if (clockTimer !== null) clearInterval(clockTimer);
    clockTimer = null;
    clockRequests.forEach(controller => controller.abort());
    clockRequests.clear();
  }
  function clockActive(generation) {
    clockRoots.forEach(root => { if (!root.isConnected) clockRoots.delete(root); });
    if (!clockRoots.size) { stopLegacyClock(); return false; }
    return generation === clockGeneration;
  }
  function updateLegacyClock() {
    const generation = clockGeneration;
    if (!clockActive(generation)) return;
    const controller = new AbortController();
    clockRequests.add(controller);
    Promise.resolve().then(() => {
      if (!clockActive(generation)) return null;
      return fetch('https://worldtimeapi.org/api/timezone/America/New_York', { signal: controller.signal });
    }).then(response => response && response.json()).then(data => {
      if (!data || !clockActive(generation)) return;
      const currentTime = new Date(data.utc_datetime);
      const date = currentTime.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
      const time = currentTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      const dateElement = document.getElementById('live-date');
      const timeElement = document.getElementById('live-time');
      if (dateElement && dateElement.isConnected) dateElement.textContent = date;
      if (timeElement && timeElement.isConnected) timeElement.textContent = time + ' ' + data.abbreviation;
    }).catch(error => {
      if (clockActive(generation)) console.error('Clock error:', error);
    }).finally(() => clockRequests.delete(controller));
  }
  function acquireLegacyClock(root) {
    clockRoots.add(root);
    if (clockTimer === null) {
      clockGeneration += 1;
      try {
        clockTimer = setInterval(updateLegacyClock, 60000);
        updateLegacyClock();
      } catch (error) {
        clockRoots.delete(root);
        stopLegacyClock();
        throw error;
      }
    }
    return () => {
      clockRoots.delete(root);
      if (!clockRoots.size) stopLegacyClock();
    };
  }

  storefront.collection = {
    isReady: root => mountedRoots.has(root),
    unregister: storefront.lifecycle.register('bs-collection', '[data-bs-collection]', function (root) {
      const owns = node => node.closest('[data-bs-collection]') === root;
      const queryAll = selector => Array.from(root.querySelectorAll(selector)).filter(owns);
      const query = selector => queryAll(selector)[0] || null;
      const configNode = query('[data-bs-collection-config]');
      if (!configNode) return;
      const config = JSON.parse(configNode.textContent);
      if (!config || typeof config !== 'object') return;

      let disposed = false;
      const cleanup = [], timers = new Set(), frames = new Set(), probes = new Set();
      function dispose() {
        if (disposed) return;
        disposed = true;
        mountedRoots.delete(root);
        timers.forEach(id => clearTimeout(id));
        frames.forEach(id => cancelAnimationFrame(id));
        timers.clear(); frames.clear();
        probes.forEach(finish => finish());
        probes.clear();
        cleanup.reverse().forEach(release => {
          try { release(); } catch (error) { console.error('Collection cleanup failed:', error); }
        });
        cleanup.length = 0;
      }
      function active() {
        if (!disposed && !root.isConnected) dispose();
        return !disposed;
      }
      function listen(target, type, handler, options) {
        const guarded = event => { if (active()) return handler(event); };
        cleanup.push(() => target.removeEventListener(type, guarded, options));
        target.addEventListener(type, guarded, options);
      }
      function later(callback, delay) {
        const id = setTimeout(() => { timers.delete(id); if (active()) callback(); }, delay);
        timers.add(id); return id;
      }
      function cancelLater(id) { clearTimeout(id); timers.delete(id); }
      function frame(callback) {
        const id = requestAnimationFrame(timestamp => { frames.delete(id); if (active()) callback(timestamp); });
        frames.add(id); return id;
      }
      function cancelFrame(id) { cancelAnimationFrame(id); frames.delete(id); }
      try {
        // Adjust cart position based on announcement bar
        function adjustCartPosition() {
          const announcementBar = query('.announcement-bar');
          const cartArea = query('.header-cart-area');

          if (announcementBar && cartArea) {
            const barHeight = announcementBar.offsetHeight;
            cartArea.style.top = (barHeight + 20) + 'px';
          }
        }

        // Run on load
        adjustCartPosition();

        // Product Card Animations
        const imageSpinEnabled = config.imageSpinEnabled;
        const spinHoverDelayMs = Math.max(0, Number(config.spinHoverDelayMs));
        const spinMobileLongPressDelayMs = Math.max(200, Number(config.spinMobileLongPressDelayMs));
        const spinFrameDurationMs = Math.max(50, Number(config.spinFrameDurationMs));
        const spinMaxLoops = Math.max(0, Number(config.spinMaxLoops));
        const spinTouchMoveTolerancePx = 12;
        const genreAutoSortEnabled = config.genreAutoSortEnabled;
        const genreSortOrder = String(config.genreSortOrder || '');
        const recentlyViewedStorageKey = 'bs_recently_viewed_products_v1';
        const recentlyViewedMaxStored = 24;
        const recentlyViewedRenderLimit = 10;

        const toHandle = (value) => String(value || '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

        const sortProductsByGenre = () => {
          if (!genreAutoSortEnabled) {
            return;
          }

          const grid = query('.advanced-product-grid');
          if (!grid) {
            return;
          }

          const genreKeys = genreSortOrder
            .split(',')
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean);

          if (!genreKeys.length) {
            return;
          }

          const genreRanks = {};
          genreKeys.forEach((genre, index) => {
            const handle = toHandle(genre);
            if (!(genre in genreRanks)) {
              genreRanks[genre] = index;
            }
            if (handle && !(handle in genreRanks)) {
              genreRanks[handle] = index;
            }
          });

          const cards = Array.from(grid.querySelectorAll('.product-card-advanced')).filter(owns);
          const getCardRank = (card) => {
            const tags = String(card.dataset.productTags || '')
              .split('|')
              .map((tag) => tag.trim().toLowerCase())
              .filter(Boolean);

            let bestRank = Number.MAX_SAFE_INTEGER;
            tags.forEach((tag) => {
              const rank = genreRanks[tag];
              const handleRank = genreRanks[toHandle(tag)];

              if (typeof rank === 'number') {
                bestRank = Math.min(bestRank, rank);
              }
              if (typeof handleRank === 'number') {
                bestRank = Math.min(bestRank, handleRank);
              }
            });

            return bestRank;
          };

          cards.sort((a, b) => {
            const rankA = getCardRank(a);
            const rankB = getCardRank(b);

            if (rankA !== rankB) {
              return rankA - rankB;
            }

            return 0;
          });

          const fragment = document.createDocumentFragment();
          cards.forEach((card) => fragment.appendChild(card));
          grid.appendChild(fragment);
        };

        const bumpNewProductsToTop = () => {
          const grid = query('.advanced-product-grid');
          if (!grid) {
            return;
          }

          const cards = Array.from(grid.querySelectorAll('.product-card-advanced')).filter(owns);
          const hasNewTag = (card) => String(card.dataset.productTags || '')
            .split('|')
            .some((tag) => tag.trim().toLowerCase() === 'new');

          const newCards = cards.filter(hasNewTag);
          if (!newCards.length) {
            return;
          }

          const fragment = document.createDocumentFragment();
          newCards.forEach((card) => fragment.appendChild(card));
          grid.insertBefore(fragment, grid.firstChild);
        };

        sortProductsByGenre();
        bumpNewProductsToTop();

        const escapeHtml = (value) => String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

        const decodeHtmlEntities = (value) => {
          const parser = document.createElement('textarea');
          parser.innerHTML = String(value || '');
          return parser.value;
        };

        const toPlainText = (value) => decodeHtmlEntities(value)
          .replace(/<[^>]*>/g, '')
          .replace(/\s+/g, ' ')
          .trim();

        const readRecentlyViewedProducts = () => {
          try {
            const raw = localStorage.getItem(recentlyViewedStorageKey);
            if (!raw) {
              return [];
            }

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) {
              return [];
            }

            return parsed.map(normalizeRecentlyViewedProduct).filter(Boolean);
          } catch (error) {
            return [];
          }
        };

        const writeRecentlyViewedProducts = (items) => {
          try {
            localStorage.setItem(recentlyViewedStorageKey, JSON.stringify(items));
          } catch (error) {
            // Ignore storage write failures (privacy mode/quota).
          }
        };

        const normalizeRecentlyViewedProduct = (productData) => {
          if (!productData || !productData.id || !productData.url || !productData.title) {
            return null;
          }

          return {
            id: String(productData.id),
            url: String(productData.url),
            title: toPlainText(productData.title),
            image: String(productData.image || ''),
            price: toPlainText(productData.price),
            compare: toPlainText(productData.compare)
          };
        };

        const getCardRecentlyViewedProduct = (card) => normalizeRecentlyViewedProduct({
          id: card.dataset.productId,
          url: card.dataset.productUrl,
          title: card.dataset.productTitle,
          image: card.dataset.productImage,
          price: card.dataset.productPrice,
          compare: card.dataset.productCompare
        });

        const pushRecentlyViewedProduct = (productData) => {
          const normalized = normalizeRecentlyViewedProduct(productData);
          if (!normalized) {
            return;
          }

          const current = readRecentlyViewedProducts();
          const deduped = current.filter((item) => String(item.id) !== normalized.id);
          deduped.unshift(normalized);
          writeRecentlyViewedProducts(deduped.slice(0, recentlyViewedMaxStored));
        };

        const buildRecentlyViewedItemMarkup = (item) => {
          const url = escapeHtml(item.url);
          const title = escapeHtml(toPlainText(item.title));
          const image = escapeHtml(item.image);
          const price = escapeHtml(toPlainText(item.price));
          const compare = escapeHtml(toPlainText(item.compare));
          const compareMarkup = compare ? `<span class="recently-viewed-compare">${compare}</span>` : '';
          const imageMarkup = image
            ? `<img src="${image}" alt="${title}" class="recently-viewed-thumb" loading="lazy">`
            : `<span class="recently-viewed-thumb recently-viewed-thumb-placeholder" aria-hidden="true"></span>`;

          return `
            <a href="${url}" class="recently-viewed-item" data-recently-viewed-link>
              ${imageMarkup}
              <span class="recently-viewed-meta">
                <span class="recently-viewed-name">${title}</span>
                <span class="recently-viewed-price-row">
                  ${compareMarkup}
                  <span class="recently-viewed-price">${price}</span>
                </span>
              </span>
            </a>
          `;
        };

        const renderRecentlyViewedStrip = () => {
          const wrap = query('[data-recently-viewed-wrap]');
          const strip = query('[data-recently-viewed-strip]');

          if (!wrap || !strip) {
            return;
          }

          const items = readRecentlyViewedProducts().slice(0, recentlyViewedRenderLimit);
          if (!items.length) {
            strip.innerHTML = '';
            wrap.hidden = true;
            return;
          }

          strip.innerHTML = items.map(buildRecentlyViewedItemMarkup).join('');
          wrap.hidden = false;
        };

        const clearRecentlyViewedButton = query('[data-recently-viewed-clear]');
        if (clearRecentlyViewedButton) {
          listen(clearRecentlyViewedButton, 'click', () => {
            writeRecentlyViewedProducts([]);
            renderRecentlyViewedStrip();
          });
        }

        const productCards = queryAll('.product-card-advanced');
        productCards.forEach((card, index) => {
          // Cap stagger delay so cards never stay invisible too long.
          // (Old code did index * 0.05s which produced 2s+ delays on long
          // collections and could leave cards stuck at opacity:0 until hover.)
          const delaySeconds = Math.min(index * 0.05, 0.6);
          const originalDelay = card.style.animationDelay;
          cleanup.push(() => { if (root.isConnected) card.style.animationDelay = originalDelay; });
          card.style.animationDelay = `${delaySeconds}s`;

          const imageContainer = card.querySelector('.product-img-container');
          const mainImage = card.querySelector('.product-main-img');
          const hoverImage = card.querySelector('.product-hover-img');
          const productLink = card.querySelector('.product-link-wrapper');

          if (!imageContainer || !mainImage) {
            return;
          }

          const spinImages = (imageContainer.dataset.spinImages || '')
            .split('||')
            .map((src) => src.trim())
            .filter(Boolean);

          if (spinImages.length < 2) {
            return;
          }

          if (!imageSpinEnabled) {
            return;
          }

          const normalizeImageSrc = (src) => (src || '')
            .split('?')[0]
            .replace(/^https?:/, '')
            .toLowerCase();

          const safeDecodeUri = (value) => {
            try {
              return decodeURIComponent(value);
            } catch (error) {
              return value;
            }
          };

          const getFilenameSortKey = (src) => {
            const cleanSrc = normalizeImageSrc(src);
            const filename = cleanSrc.split('/').pop() || cleanSrc;
            return safeDecodeUri(filename);
          };

          const orderedSpinImages = [...spinImages].sort((a, b) =>
            getFilenameSortKey(a).localeCompare(getFilenameSortKey(b), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          );

          const normalizedOrderedSpinImages = orderedSpinImages.map(normalizeImageSrc);
          const originalImageSrc = mainImage.getAttribute('src');
          const frameDurationMs = spinFrameDurationMs;
          const isHalfSpin = imageContainer.dataset.spinHalf === 'true';
          let hoverDelayTimer = null;
          let spinRaf = null;
          let lastFrameTimestamp = 0;
          let isSpinning = false;
          let preloaded = false;
          let spinIndex = 0;
          let spinDirection = 1;
          let completedLoops = 0;
          let isTouchPressing = false;
          let touchStartX = 0;
          let touchStartY = 0;
          let touchLongPressTriggered = false;
          const saveCardAsRecentlyViewed = () => {
            const productData = getCardRecentlyViewedProduct(card);
            if (!productData) {
              return;
            }

            pushRecentlyViewedProduct(productData);
          };

          let preloadPromise = null;
          let interactionGeneration = 0;
          const preloadSpinImages = () => {
            if (preloaded) return Promise.resolve();
            if (preloadPromise) return preloadPromise;
            preloadPromise = Promise.all(orderedSpinImages.map((src) => new Promise((resolve) => {
              const image = new Image();
              let settled = false;
              const finish = () => {
                if (settled) return;
                settled = true;
                image.onload = image.onerror = null;
                probes.delete(finish);
                resolve();
              };
              probes.add(finish);
              image.decoding = 'async';
              image.onload = image.onerror = finish;
              image.src = src;
              if (image.complete) finish();
            }))).then(() => { if (active()) preloaded = true; });
            return preloadPromise;
          };

          const runSpinFrame = (timestamp) => {
            if (!isSpinning) {
              return;
            }

            if (!lastFrameTimestamp) {
              lastFrameTimestamp = timestamp;
            }

            if (timestamp - lastFrameTimestamp >= frameDurationMs) {
              let nextIndex;

              if (isHalfSpin && orderedSpinImages.length > 1) {
                nextIndex = spinIndex + spinDirection;

                if (nextIndex >= orderedSpinImages.length) {
                  spinDirection = -1;
                  nextIndex = orderedSpinImages.length - 2;
                } else if (nextIndex < 0) {
                  spinDirection = 1;
                  nextIndex = 1;
                  completedLoops += 1;

                  if (spinMaxLoops > 0 && completedLoops >= spinMaxLoops) {
                    stopSpin();
                    return;
                  }
                }
              } else {
                nextIndex = (spinIndex + 1) % orderedSpinImages.length;

                if (nextIndex === 0) {
                  completedLoops += 1;

                  if (spinMaxLoops > 0 && completedLoops >= spinMaxLoops) {
                    stopSpin();
                    return;
                  }
                }
              }

              spinIndex = nextIndex;
              mainImage.setAttribute('src', orderedSpinImages[spinIndex]);
              lastFrameTimestamp = timestamp;
            }

            spinRaf = frame(runSpinFrame);
          };

          const stopSpin = () => {
            interactionGeneration += 1;
            if (hoverDelayTimer) {
              cancelLater(hoverDelayTimer);
              hoverDelayTimer = null;
            }

            if (spinRaf) {
              cancelFrame(spinRaf);
              spinRaf = null;
            }

            isSpinning = false;
            lastFrameTimestamp = 0;
            completedLoops = 0;
            if (root.isConnected) {
              card.classList.remove('is-spinning');
              mainImage.setAttribute('src', originalImageSrc);
            }
          };

          cleanup.push(stopSpin);

          const startSpin = () => {
            if (!active() || isSpinning) {
              return;
            }

            const hoverSrc = hoverImage ? hoverImage.getAttribute('src') : '';
            const initialSrc = hoverSrc || mainImage.getAttribute('src') || originalImageSrc;
            const visibleIndex = normalizedOrderedSpinImages.indexOf(normalizeImageSrc(initialSrc));

            spinIndex = visibleIndex !== -1 ? visibleIndex : 0;
            spinDirection = 1;
            mainImage.setAttribute('src', orderedSpinImages[spinIndex]);
            completedLoops = 0;
            isSpinning = true;
            card.classList.add('is-spinning');
            spinRaf = frame(runSpinFrame);
          };

          const queueSpinStart = (delayMs, shouldStart, onStart) => {
            if (hoverDelayTimer || isSpinning) {
              return;
            }

            const generation = ++interactionGeneration;
            hoverDelayTimer = later(() => {
              hoverDelayTimer = null;

              preloadSpinImages().then(() => {
                if (active() && generation === interactionGeneration && shouldStart()) {
                  if (onStart) {
                    onStart();
                  }

                  startSpin();
                }
              });
            }, delayMs);
          };

          const cancelPendingSpinStart = () => {
            interactionGeneration += 1;
            if (hoverDelayTimer) {
              cancelLater(hoverDelayTimer);
              hoverDelayTimer = null;
            }
          };

          listen(card, 'mouseenter', () => {
            queueSpinStart(spinHoverDelayMs, () => card.matches(':hover'));
          });

          listen(card, 'mouseleave', stopSpin);

          listen(card, 'touchstart', (event) => {
            if (event.touches.length !== 1) {
              return;
            }

            const touch = event.touches[0];
            isTouchPressing = true;
            touchLongPressTriggered = false;
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;

            queueSpinStart(
              spinMobileLongPressDelayMs,
              () => isTouchPressing,
              () => {
                touchLongPressTriggered = true;
              }
            );
          }, { passive: true });

          listen(card, 'touchmove', (event) => {
            if (!isTouchPressing || event.touches.length !== 1) {
              return;
            }

            const touch = event.touches[0];
            const movedX = Math.abs(touch.clientX - touchStartX);
            const movedY = Math.abs(touch.clientY - touchStartY);

            if (movedX > spinTouchMoveTolerancePx || movedY > spinTouchMoveTolerancePx) {
              isTouchPressing = false;
              cancelPendingSpinStart();

              if (isSpinning) {
                stopSpin();
              }
            }
          }, { passive: true });

          const endTouchInteraction = () => {
            isTouchPressing = false;

            if (isSpinning) {
              stopSpin();
              return;
            }

            cancelPendingSpinStart();
          };

          listen(card, 'touchend', endTouchInteraction, { passive: true });
          listen(card, 'touchcancel', endTouchInteraction, { passive: true });

          if (productLink) {
            listen(productLink, 'click', (event) => {
              if (touchLongPressTriggered) {
                event.preventDefault();
                touchLongPressTriggered = false;
                return;
              }

              saveCardAsRecentlyViewed();
            });

            listen(productLink, 'auxclick', (event) => {
              if (event.button === 1) {
                saveCardAsRecentlyViewed();
              }
            });
          }
        });

        renderRecentlyViewedStrip();

        const applySwatchSelectionToQuickAdd = (card, swatchButton) => {
          if (!card || !swatchButton) {
            return;
          }

          const quickAddForm = card.querySelector('[data-quick-add-form]');
          if (!quickAddForm) {
            return;
          }

          const variantInput = quickAddForm.querySelector('input[name="id"]');
          const quickAddButton = quickAddForm.querySelector('.quick-add-btn');
          if (!variantInput || !quickAddButton) {
            return;
          }

          const variantId = swatchButton.dataset.variantId;
          const isAvailable = swatchButton.dataset.variantAvailable === 'true';

          if (!variantId) {
            quickAddButton.disabled = true;
            quickAddButton.classList.add('is-unavailable');
            quickAddButton.setAttribute('aria-disabled', 'true');
            return;
          }

          variantInput.value = variantId;
          quickAddButton.classList.remove('is-adding', 'is-added', 'is-error');
          quickAddButton.textContent = '+';
          quickAddButton.disabled = !isAvailable;
          quickAddButton.classList.toggle('is-unavailable', !isAvailable);
          quickAddButton.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
          if (submissions.get(quickAddForm)?.pending) {
            quickAddButton.disabled = true;
            quickAddButton.classList.add('is-adding');
          }
        };

        const initializeVariantSwatches = () => {
          const cards = queryAll('.product-card-advanced');

          cards.forEach((card) => {
            const swatches = Array.from(card.querySelectorAll('.variant-swatch-dot[data-variant-id]'));
            if (!swatches.length) {
              return;
            }

            const quickAddForm = card.querySelector('[data-quick-add-form]');
            const variantInput = quickAddForm ? quickAddForm.querySelector('input[name="id"]') : null;
            const selectedVariantId = variantInput ? variantInput.value : '';

            let activeSwatch = swatches.find((swatch) => swatch.classList.contains('is-active'));
            if (!activeSwatch && selectedVariantId) {
              activeSwatch = swatches.find((swatch) => swatch.dataset.variantId === selectedVariantId);
            }
            if (!activeSwatch) {
              activeSwatch = swatches.find((swatch) => swatch.dataset.variantAvailable === 'true') || swatches[0];
            }

            const setActiveSwatch = (targetSwatch) => {
              swatches.forEach((swatch) => {
                const isActive = swatch === targetSwatch;
                swatch.classList.toggle('is-active', isActive);
                swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
              });

              applySwatchSelectionToQuickAdd(card, targetSwatch);
            };

            if (activeSwatch) {
              setActiveSwatch(activeSwatch);
            }

            swatches.forEach((swatch) => {
              listen(swatch, 'click', () => {
                setActiveSwatch(swatch);
              });
            });
          });
        };

        initializeVariantSwatches();

        const quickAddForms = queryAll('[data-quick-add-form]');
        quickAddForms.forEach((form) => {
          const button = form.querySelector('.quick-add-btn');
          if (!button) return;
          let record = submissions.get(form);
          if (!record) {
            record = { pending: false, notify: null };
            submissions.set(form, record);
          }
          let feedbackTimer = null;
          const available = () => {
            const card = form.closest('.product-card-advanced');
            const swatch = card && card.querySelector('.variant-swatch-dot.is-active');
            return !swatch || (!!swatch.dataset.variantId && swatch.dataset.variantAvailable === 'true');
          };
          const resetFeedback = () => {
            button.classList.remove('is-adding', 'is-added', 'is-error');
            button.textContent = '+';
            button.disabled = record.pending || !available();
          };
          const notify = (success) => {
            if (!active()) return;
            button.classList.remove('is-adding');
            button.classList.add(success ? 'is-added' : 'is-error');
            button.textContent = success ? '✓' : '!';
            feedbackTimer = later(resetFeedback, 900);
          };
          record.notify = notify;
          if (record.pending) {
            button.disabled = true;
            button.classList.add('is-adding');
          }
          cleanup.push(() => {
            if (record.notify === notify) record.notify = null;
            if (root.isConnected) resetFeedback();
          });
          listen(form, 'submit', (event) => {
            event.preventDefault();
            if (record.pending || button.disabled) return;

            const formData = new FormData(form);
            const body = new URLSearchParams();
            for (const [key, value] of formData.entries()) body.append(key, String(value));

            cancelLater(feedbackTimer);
            record.pending = true;
            button.disabled = true;
            button.classList.add('is-adding');
            sendQuickAdd(record, body.toString());
          });
        });

        cleanup.push(acquireLegacyClock(root));
      } catch (error) {
        dispose();
        throw error;
      }
      mountedRoots.add(root);
      return dispose;
    })
  };
})();
